import { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { API_BASE_URL } from '../../config';
import Avatar from './ui/Avatar';
import CardState from './ui/CardState';

/**
 * The signed-in employee's own reporting line: the chain from the top of the
 * org down to them, plus their direct reports.
 *
 * Self-only. The server has no per-id variant, so this component is only ever
 * rendered on the person's own profile — see ProfilePage's !isMemberView gate.
 */

function Node({ person, depth, variant }) {
  // The shared Avatar owns photo-or-initials and the broken-image fallback,
  // so every avatar on the profile degrades the same way.
  const avatar = (
    <Avatar
      name={person.name}
      src={person.photo_url}
      size="node"
      variant="hashed"
      className="rl-node__avatar"
    />
  );

  const label = (
    <>
      {avatar}
      <span className="rl-node__text">
        <span className="rl-node__name">{person.name}</span>
        {person.job_title && <span className="rl-node__job">{person.job_title}</span>}
      </span>
      {/* Inside the tinted row: as a sibling of it the marker wrapped onto a
          line of its own below the highlight and read as orphaned. */}
      {variant === 'self' && <span className="rl-node__you">You</span>}
    </>
  );

  return (
    <li
      className={`rl-node rl-node--${variant}`}
      style={{ '--rl-depth': depth }}
    >
      {/* Only link to a profile that actually exists in Nexus. An Odoo-only
          colleague still renders, just not as a link. */}
      {person.user_id && variant !== 'self' ? (
        <Link className="rl-node__body" to={`/profile/${person.user_id}`}>{label}</Link>
      ) : (
        <span className="rl-node__body">{label}</span>
      )}
    </li>
  );
}

export default function ReportingLine() {
  const [state, setState] = useState('loading'); // loading | ok | empty | error
  const [data, setData] = useState(null);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const r = await axios.get(`${API_BASE_URL}/api/profile/reporting-line`, {
          withCredentials: true,
        });
        if (!active) return;
        if (!r.data.available || !(r.data.chain || []).length) {
          setState('empty');
          return;
        }
        setData(r.data);
        setState('ok');
      } catch {
        if (active) setState('error');
      }
    })();
    return () => { active = false; };
  }, []);

  if (state === 'loading') {
    return (
      <section className="profile-info-card profile-info-card--span">
        <header className="profile-info-card__head">
          <h2 className="profile-info-card__title">Reporting line</h2>
        </header>
        <CardState kind="loading" label="Loading reporting line" />
      </section>
    );
  }

  // A missing reporting line is not worth an error card on someone's profile:
  // it just means Odoo has no manager recorded for them yet.
  if (state !== 'ok') return null;

  const { chain, reports } = data;

  return (
    <section className="profile-info-card profile-info-card--span">
      <header className="profile-info-card__head">
        <h2 className="profile-info-card__title">Reporting line</h2>
        <span className="profile-source-tag">Synced from Odoo</span>
      </header>

      <ol className="rl-tree">
        {chain.map((p, i) => (
          <Node
            key={p.odoo_employee_id}
            person={p}
            depth={i}
            variant={p.is_self ? 'self' : 'manager'}
          />
        ))}
        {(reports || []).map((p) => (
          <Node
            key={p.odoo_employee_id}
            person={p}
            depth={chain.length}
            variant="report"
          />
        ))}
      </ol>

      {reports && reports.length > 0 && (
        <p className="rl-foot">
          {reports.length} direct {reports.length === 1 ? 'report' : 'reports'}
        </p>
      )}
    </section>
  );
}
